<div *transloco="let t">
	@if (finishedLoading()) {
		<div class="tenant-unit-reference-container">
			@if (selectedInstance()) {
				@if (lockPortalAccessUntil) {
					<div class="flex flex-row content-center space-x-2 pb-3">
						<mat-icon>info</mat-icon>
						<div>
							{{ t('billing.lockPortalAccessAllowed', { dateAllowed: dateUtil.formatDate(dayjs(lockPortalAccessUntil).add(1, 'day'), false) }) }}
						</div>
					</div>
				}
				<!-- 1. General Timeline (Current Instance) -->
				<div class="flex items-center justify-end gap-2 mb-2">
					<mat-form-field appearance="outline" class="w-40 tenant-unit-select" subscriptSizing="dynamic">
						<mat-label>{{ t('tenantunitDetails.details.validFrom') }}</mat-label>
						<input [matDatepicker]="dpFrom" [ngModel]="tenantUnitDateRangeFilter.validFrom" matInput disabled />
						<mat-datepicker-toggle [for]="dpFrom" matSuffix></mat-datepicker-toggle>
						<mat-datepicker #dpFrom></mat-datepicker>
					</mat-form-field>
					<mat-form-field appearance="outline" class="w-40 tenant-unit-select" subscriptSizing="dynamic">
						<mat-label>{{ t('tenantunitDetails.details.validTo') }}</mat-label>
						<input [matDatepicker]="dpTo" [ngModel]="tenantUnitDateRangeFilter.validTo" matInput disabled />
						<mat-datepicker-toggle [for]="dpTo" matSuffix></mat-datepicker-toggle>
						<mat-datepicker #dpTo></mat-datepicker>
					</mat-form-field>
					<button mat-icon-button type="button" (click)="openDateRangeFilterDialog()" [matTooltip]="t('tenantunitDetails.dateRangeFilter.title')">
						<mat-icon [svgIcon]="icons.get('filter')"></mat-icon>
					</button>
				</div>
				<div class="gantt-section-compact">
					<ara-parameter-value-gantt
						[viewStartDate]="startDate()"
						[viewEndDate]="endDate()"
						[barTextStyle]="myBarTextStyle"
						[rowHeight]="GANTT_ROW_HEIGHT_MAIN"
						[barHeight]="GANTT_BAR_HEIGHT"
						[axisOffset]="GANTT_AXIS_OFFSET"
						axisPosition="top"
						[chartData]="ganttDataGeneral"
						[showAddBtn]="false"
						[enableAxisDrag]="true"
						[disableAnimation]="isDraggingTimeline()"
						(dateRangeShift)="handleDateRangeShift($event)"
						(dragStart)="onDragStart()"
						(dragEnd)="onDragEnd()"></ara-parameter-value-gantt>
				</div>

				<!-- 2. Common Parameters (Share, Selection, Tariff) -->
				<div class="section-header">
					<span class="section-title">
						<mat-icon aria-hidden="true">settings</mat-icon>
						{{ t('billing.parameterDefinition') }}
					</span>
				</div>
				<div class="gantt-section-compact">
					<!-- Shares -->
					<ara-parameter-value-gantt
						[viewStartDate]="startDate()"
						[viewEndDate]="endDate()"
						[barTextStyle]="myBarTextStyle"
						[rowHeight]="GANTT_ROW_HEIGHT"
						[barHeight]="GANTT_BAR_HEIGHT"
						[axisOffset]="GANTT_AXIS_OFFSET"
						axisPosition="none"
						[chartData]="processedGanttDataShare"
						[parameterValueType]="ParameterValueType.SHARE"
						[disableAnimation]="isDraggingTimeline()"
						(rowActionClick)="addParameterValueByType($event)"
						(ganttSegmentClick)="editParameterValueBySegment($event)"></ara-parameter-value-gantt>

					<!-- Selections -->
					<ara-parameter-value-gantt
						[viewStartDate]="startDate()"
						[viewEndDate]="endDate()"
						[barTextStyle]="myBarTextStyle"
						[rowHeight]="GANTT_ROW_HEIGHT"
						[barHeight]="GANTT_BAR_HEIGHT"
						[axisOffset]="GANTT_AXIS_OFFSET"
						axisPosition="none"
						[chartData]="processedGanttDataExemption"
						[parameterValueType]="ParameterValueType.SELECTION"
						[disableAnimation]="isDraggingTimeline()"
						(rowActionClick)="addParameterValueByType($event)"
						(ganttSegmentClick)="editParameterValueBySegment($event)"></ara-parameter-value-gantt>

					<!-- Tariffs -->
					<ara-parameter-value-gantt
						[viewStartDate]="startDate()"
						[viewEndDate]="endDate()"
						[barTextStyle]="myBarTextStyle"
						[rowHeight]="GANTT_ROW_HEIGHT"
						[barHeight]="GANTT_BAR_HEIGHT"
						[axisOffset]="GANTT_AXIS_OFFSET"
						axisPosition="none"
						[chartData]="processedGanttDataTariff"
						[parameterValueType]="ParameterValueType.TARIFF"
						[disableAnimation]="isDraggingTimeline()"
						(rowActionClick)="addParameterValueByType($event)"
						(ganttSegmentClick)="editParameterValueBySegment($event)"></ara-parameter-value-gantt>
				</div>

				<!-- 3. Roles Sections (Tenant, Resident, Owner) -->
				<div class="section-header">
					<span class="section-title">
						<mat-icon aria-hidden="true">groups</mat-icon>
						{{ t('billing.roles') }}
					</span>
				</div>

				@for (group of roleGroups; track group.roleTypeKey) {
					<div class="role-section">
						<!-- Rollengruppen-Header: links Rollenname, Mitte Nutzer-Bars, rechts Expand -->
						<div class="role-header-with-gantt" (click)="toggleRoleSection(group.roleTypeKey)">
							<div class="role-label">
								<mat-icon>person</mat-icon>
								<span>{{ group.displayLabel }}</span>
							</div>
							<div class="role-gantt-preview">
								<ara-parameter-value-gantt
									[viewStartDate]="startDate()"
									[viewEndDate]="endDate()"
									[barTextStyle]="myBarTextStyle"
									[rowHeight]="GANTT_ROW_HEIGHT"
									[barHeight]="GANTT_BAR_HEIGHT"
									[axisOffset]="GANTT_AXIS_OFFSET"
									axisPosition="none"
									[chartData]="group.userGanttRows"
									[parameterValueType]="ParameterValueType.ROLE"
									[showAddBtn]="false"
									[showLabel]="false"
									[reserveActionsSpace]="false"
									[allowSegmentClick]="false"
									[disableAnimation]="isDraggingTimeline()"></ara-parameter-value-gantt>
							</div>
							<mat-icon class="expand-icon" [class.expanded]="isRoleExpanded(group.roleTypeKey)">expand_more</mat-icon>
						</div>

						<!-- Aufgeklappter Bereich: Vorauszahlungen und Einstellungen dieser Rolle -->
						@if (isRoleExpanded(group.roleTypeKey)) {
							<div class="role-content" [@fadeInUp]>
								<!-- Vorauszahlungen für diese Rolle -->
								<div class="section-header section-header-sub">
									<span class="section-title">
										<mat-icon aria-hidden="true">payments</mat-icon>
										{{ t('billing.payments') }}
									</span>
								</div>
								<div class="gantt-section-compact">
									<ara-parameter-value-gantt
										[viewStartDate]="startDate()"
										[viewEndDate]="endDate()"
										[barTextStyle]="myBarTextStyle"
										[rowHeight]="GANTT_ROW_HEIGHT"
										[barHeight]="GANTT_BAR_HEIGHT"
										[axisOffset]="GANTT_AXIS_OFFSET"
										axisPosition="none"
										[chartData]="group.paymentGanttRows"
										[parameterValueType]="ParameterValueType.PAYMENT"
										[disableAnimation]="isDraggingTimeline()"
										(rowActionClick)="addParameterValueByType($event)"
										(ganttSegmentClick)="editParameterValueBySegment($event, group)"></ara-parameter-value-gantt>
								</div>

								<!-- Einstellungen für diese Rolle -->
								<div class="section-header section-header-sub">
									<span class="section-title">
										<mat-icon aria-hidden="true">settings</mat-icon>
										{{ t('billing.settings') }}
									</span>
								</div>
								<div class="gantt-section-compact">
									<ara-parameter-value-gantt
										[viewStartDate]="startDate()"
										[viewEndDate]="endDate()"
										[barTextStyle]="myBarTextStyle"
										[rowHeight]="GANTT_ROW_HEIGHT"
										[barHeight]="GANTT_BAR_HEIGHT"
										[axisOffset]="GANTT_AXIS_OFFSET"
										axisPosition="none"
										[chartData]="group.settingGanttRows"
										[parameterValueType]="ParameterValueType.SETTING"
										[disableAnimation]="isDraggingTimeline()"
										(rowActionClick)="addParameterValueByType($event)"
										(ganttSegmentClick)="editParameterValueBySegment($event, group)"></ara-parameter-value-gantt>
								</div>
							</div>
						}
					</div>
				} @empty {
					<div class="role-section role-section-empty">
						<div class="role-header-empty">
							<mat-icon>info</mat-icon>
							<span>{{ t('billing.noRolesAvailable') }}</span>
						</div>
					</div>
				}
			}
		</div>
	}
</div>
